import ButtonTsoPq from '@components/Buttons/ButtonTsoPq';
import SegmentedButtonTsoPq from '@components/Buttons/SegmentedButtonTsoPq';
import ChipSelectorTsoPq from '@components/ChipSelector/ChipSelectorTsoPq';
import DatepickerTsoPq from '@components/Datepicker/DatepickerTsoPq';
import ModalDialogTsoPq from '@components/ModalDialog/ModalDialogTsoPq';
import TextFieldTsoPq from '@components/TextField/TextFieldTsoPq';
import TreeAutocompleteTsoPq from '@components/TreeAutocomplete/TreeAutocompleteTsoPq';
import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import { parseSubstationDevices } from '@custom-types/TreeTypes';
import {
  StyledButtonWrapper,
  StyledLineWrapper,
  StyledMeasurementDeviceTitle,
  StyledWrapper,
} from '@pages/MeasurementVisualization/Section/MeasurementVisualizationParameters';
import { TsosStateProps } from '@pages/MeasurementVisualization/utils';
import { StyledSectionWrapper } from '@pages/MeasurmentExport/MeasurmentExportStyles';
import {
  ApiApplicationServicesDataTypesCorrelationTypesNewCorrelationJob,
  ApiDatabaseCommonCorrelationJobType,
  usePostCorrelationJobSubmitMutation,
} from '@redux/apiSlices/correlationJobApi';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { useSidePanel } from '@components/Grid/GridTsoPq';
import {
  ApiCommonExternalDtoMeasurementParameterExternalDto,
  useGetMeasurementParameterGetAllQuery,
} from '@redux/apiSlices/measurementParameterApi';
import { useLazyGetSubstationGetSubstationDeviceHierarchyQuery } from '@redux/apiSlices/substationApi';
import { useGetTsoGetAllQuery } from '@redux/apiSlices/tsoApi';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import {
  resetAll,
  selectActiveTsos,
  selectTriedSubmitting,
  setActiveTsoName,
  setActiveTsos,
  setCalculationName,
  setDeviceTechnicalNames,
  setEnd,
  setMandatoryFieldsStatus,
  setParameterSelection,
  setSelectedCalculation,
  setSelectedParameters,
  setStart,
  setThreshold,
  setTriedSubmitting,
  setType,
  validateMandatoryFields,
} from '@redux/slices/correlationSlice';
import { setSnackbar } from '@redux/slices/snackbarSlice';
import { TreeListItem, useLtreeTreeListFromObjects } from '@utils/hooks/useTransfromLtreeInHyerarchy';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PermissionGate from '@components/PermissionGate/PermissionGate';
import { userOrSupportRestriction } from '@components/PermissionGate/RolePermissionConfig';

const calculationNameValidationRegex = /^[\p{L}\p{N}\s()\/._-]+$/u;

const isThresholdInRange = (threshold: string | undefined) => {
  if (!threshold) {
    return true;
  }

  const number = Number(threshold);
  return !Number.isNaN(number) && Number.isFinite(number) && number >= 0 && number <= 1;
};

const isSameSelection = (left: any[] = [], right: any[] = []) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const CorelationParameters = () => {
  const { handleOpen } = useSidePanel();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [thresholdValidation, setThresholdValidation] = useState<boolean>(true);
  const [showSubmitModal, setShowSubmitModal] = useState<boolean>(false);
  const [tsoDeviceOptions, setTsoDeviceOptions] = useState<{
    [key: number]: TreeListItem[] | undefined;
  }>({});
  const [parameterOptions, setParameterOptions] = useState<TreeListItem[] | undefined>([]);
  const [calculationNameError, setCalculationNameError] = useState<string | null>(null);

  const [localChanges, setLocalChanges] = useState<{
    start?: string;
    end?: string;
    deviceTechnicalNames?: { [key: number]: string[] };
    selectedParameters?: string[];
    threshold?: string;
    type?: number;
  }>({});

  const { data: tsos } = useGetTsoGetAllQuery();
  const [triggerGetDeviceHierarchy] = useLazyGetSubstationGetSubstationDeviceHierarchyQuery();
  const { data: measurementParameters } = useGetMeasurementParameterGetAllQuery();
  const [submitSelectionForCorrelation] = usePostCorrelationJobSubmitMutation();

  const activeTsos = useAppSelector(selectActiveTsos);
  const activeTsoName = useAppSelector(selectActiveTsos).at(-1)?.name ?? '';
  const selectedCalculation = useAppSelector((state) => state.correlation.selectedCalculation);
  const activeCalculationParameters = useAppSelector((state) => state.correlation.activeCalculationParameters);
  const [modalCalculationName, setModalCalculationName] = useState(activeCalculationParameters.calculationName ?? '');

  const prevActiveTsosRef = useRef<TsosStateProps[]>([]);
  const devicesLoaded = useRef<boolean>(false);
  const pendingInitTsosRef = useRef<Set<string>>(new Set());

  const filteredParameters = useMemo(
    () => measurementParameters?.externalDtos?.filter((p) => p.isUsedForDailyCorrelationCalculator),
    [measurementParameters?.externalDtos]
  );
  const parameters = useLtreeTreeListFromObjects(filteredParameters);

  const mandatoryFieldsStatus = useAppSelector(
    (state: { correlation: { mandatoryFieldsStatus: any } }) => state.correlation.mandatoryFieldsStatus
  );

  const triedSubmitting = useAppSelector(selectTriedSubmitting);

  const clearSelectedCalculationOnUserEdit = () => {
    if (selectedCalculation) {
      dispatch(setSelectedCalculation(undefined));
    }
  };

  const handleStartChange = (date: dayjs.Dayjs) => {
    clearSelectedCalculationOnUserEdit();
    setLocalChanges((prev) => ({ ...prev, start: dayjs(date).format('YYYY-MM-DD HH:mm') }));
    dispatch(setStart(dayjs(date).format('YYYY-MM-DD HH:mm')));
    const { mandatoryFieldsStatus } = validateMandatoryFields(activeCalculationParameters);
    dispatch(setMandatoryFieldsStatus({ ...mandatoryFieldsStatus, start: !!date }));
  };

  const handleEndChange = (date: dayjs.Dayjs) => {
    clearSelectedCalculationOnUserEdit();
    setLocalChanges((prev) => ({ ...prev, end: dayjs(date).format('YYYY-MM-DD HH:mm') }));
    dispatch(setEnd(dayjs(date).format('YYYY-MM-DD HH:mm')));
    const { mandatoryFieldsStatus } = validateMandatoryFields(activeCalculationParameters);
    dispatch(setMandatoryFieldsStatus({ ...mandatoryFieldsStatus, end: !!date }));
  };

  const handleActiveTsosChange = (selectedTsos: TsosStateProps[]) => {
    clearSelectedCalculationOnUserEdit();
    const lastActiveTsoName = selectedTsos.at(-1)?.name ?? '';
    const selectedTsoIds = new Set(selectedTsos.map((tso) => String(tso.id)));
    const updatedDeviceTechnicalNames = { ...activeCalculationParameters.deviceTechnicalNames };

    for (const tsoId of Object.keys(updatedDeviceTechnicalNames)) {
      if (!selectedTsoIds.has(tsoId)) delete updatedDeviceTechnicalNames[tsoId];
    }

    dispatch(setDeviceTechnicalNames(updatedDeviceTechnicalNames));
    dispatch(setActiveTsos(selectedTsos));
    dispatch(setActiveTsoName(lastActiveTsoName));

    const { mandatoryFieldsStatus } = validateMandatoryFields(activeCalculationParameters);
    dispatch(setMandatoryFieldsStatus({ ...mandatoryFieldsStatus, tso: selectedTsos.length > 0 }));
  };

  const parameterTypeOptions = [
    {
      segmentValue: 'single',
      segmentLabel: t('correlation.singleParameter'),
    },
    {
      segmentValue: 'multiple',
      segmentLabel: t('correlation.multipleParameters'),
    },
  ];

  const handleParameterTypeChange = (selectedValues: string[]) => {
    const selected = selectedValues[0] as 'single' | 'multiple' | undefined;

    if (!selected || selected === activeCalculationParameters.parameterType) return;

    clearSelectedCalculationOnUserEdit();
    dispatch(setParameterSelection(selected));

    const typeValue = selected === 'single' ? 0 : 1;

    setLocalChanges((prev) => ({
      ...prev,
      type: typeValue,
      selectedParameters: [],
    }));

    dispatch(setType(typeValue));
    dispatch(setSelectedParameters([]));
    const { mandatoryFieldsStatus } = validateMandatoryFields(activeCalculationParameters);
    dispatch(setMandatoryFieldsStatus({ ...mandatoryFieldsStatus, selectedParameters: selectedValues }));
  };

  const handleParametersChange = (selectedParameters: string[], _checked?: string[], isUserChange = true) => {
    if (!isUserChange && selectedCalculation) {
      return;
    }

    if (isUserChange) {
      clearSelectedCalculationOnUserEdit();
    }

    setLocalChanges((prev) => ({
      ...prev,
      selectedParameters,
    }));
    dispatch(setSelectedParameters(selectedParameters));
    const { mandatoryFieldsStatus } = validateMandatoryFields(activeCalculationParameters);
    dispatch(setMandatoryFieldsStatus({ ...mandatoryFieldsStatus, selectedParameters }));
  };

  const handleThresholdChange = (thresholdValue: string) => {
    clearSelectedCalculationOnUserEdit();
    setThresholdValidation(isThresholdInRange(thresholdValue));
    setLocalChanges((prev) => ({ ...prev, threshold: thresholdValue }));
    dispatch(setThreshold(thresholdValue));
  };

  const handleDevicesChange = (id: number, selectedDevices: string[], isUserChange = false) => {
    const tsoKey = id.toString();
    const prevDevicesForTso = activeCalculationParameters.deviceTechnicalNames[tsoKey] ?? [];
    const isSameAsStore = isSameSelection(prevDevicesForTso, selectedDevices);

    if (isSameAsStore && pendingInitTsosRef.current.has(tsoKey)) {
      pendingInitTsosRef.current.delete(tsoKey);

      if (pendingInitTsosRef.current.size === 0) {
        devicesLoaded.current = true;
      }
    }

    if (isUserChange && selectedCalculation && !devicesLoaded.current) {
      devicesLoaded.current = true;
      pendingInitTsosRef.current.clear();
    }

    if (isUserChange) {
      clearSelectedCalculationOnUserEdit();
    }

    if (!isUserChange && selectedCalculation) return;

    const updatedDeviceTechnicalNames = {
      ...activeCalculationParameters.deviceTechnicalNames,
      [tsoKey]: selectedDevices,
    };

    if (selectedDevices.length === 0) {
      updatedDeviceTechnicalNames[tsoKey] = [];
    }

    dispatch(setDeviceTechnicalNames(updatedDeviceTechnicalNames));

    setLocalChanges((prev) => ({
      ...prev,
      deviceTechnicalNames: {
        ...prev.deviceTechnicalNames,
        [tsoKey]: selectedDevices,
      },
    }));

    const { mandatoryFieldsStatus } = validateMandatoryFields(activeCalculationParameters);
    dispatch(setMandatoryFieldsStatus({ ...mandatoryFieldsStatus, selectedDevices: updatedDeviceTechnicalNames }));
  };

  const handleResetInModal = () => {
    setShowSubmitModal(false);
    setModalCalculationName('');
  };

  const handleReset = () => {
    dispatch(resetAll());
  };

  const handleSubmit = () => {
    dispatch(setTriedSubmitting(true));

    const { mandatoryFieldsStatus, allValid } = validateMandatoryFields(activeCalculationParameters);
    dispatch(setMandatoryFieldsStatus(mandatoryFieldsStatus));

    if (allValid) {
      setShowSubmitModal(true);
    } else return;
  };

  const getSelectedParameterIds = (): number[] => {
    if (!measurementParameters?.externalDtos || !activeCalculationParameters.selectedParameters.length) return [];

    const lookup = new Map(
      measurementParameters.externalDtos
        .filter((p): p is ApiCommonExternalDtoMeasurementParameterExternalDto & { id: number } => !!p.id)
        .map((p) => [p.technicalName, p.id])
    );

    const ids = activeCalculationParameters.selectedParameters
      .map((techName) => lookup.get(techName))
      .filter((id): id is number => typeof id === 'number');

    return ids;
  };

  const validateName = (name: string) => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      return t('correlation.dialogTextfieldValidationInvalidName');
    }

    if (!calculationNameValidationRegex.test(trimmedName)) {
      return t('correlation.dialogTextfieldValidationError');
    }

    return null;
  };

  function applyLocalChanges() {
    if (!Object.keys(localChanges).length) return;

    const { start, end, type, threshold, selectedParameters, deviceTechnicalNames } = localChanges;

    if (start) dispatch(setStart(start));
    if (end) dispatch(setEnd(end));
    if (type !== undefined) dispatch(setType(type));
    if (threshold) dispatch(setThreshold(threshold));
    if (selectedParameters) dispatch(setSelectedParameters(selectedParameters));

    if (deviceTechnicalNames) {
      for (const [tsoId, techNames] of Object.entries(deviceTechnicalNames)) {
        dispatch(
          setDeviceTechnicalNames({
            ...activeCalculationParameters.deviceTechnicalNames,
            [tsoId]: techNames,
          })
        );
      }
    }
  }

  const handleCalculationSubmit = async () => {
    dayjs.extend(utc);

    const sanitizedCalculationName = modalCalculationName?.trim() ?? '';
    const error = validateName(sanitizedCalculationName);

    if (error) {
      setCalculationNameError(error);
      return;
    }

    setCalculationNameError(null);
    dispatch(setCalculationName(sanitizedCalculationName));
    applyLocalChanges();
    setShowSubmitModal(false);

    const allDeviceTechNames = activeCalculationParameters.deviceTechnicalNames ?? {};

    const selectedDeviceIds = Object.values(allDeviceTechNames)
      .filter(Array.isArray)
      .flat()
      .filter((fullId): fullId is string => !!fullId)
      .map((fullId) => Number(fullId.split('-').at(-1)))
      .filter(Boolean);

    const parameterIds = getSelectedParameterIds();
    if (!parameterIds.length) {
      return;
    }

    const payload: ApiApplicationServicesDataTypesCorrelationTypesNewCorrelationJob = {
      name: sanitizedCalculationName,
      start: dayjs(localChanges.start ?? activeCalculationParameters.start)
        .utc()
        .toISOString(),
      end: dayjs(localChanges.end ?? activeCalculationParameters.end)
        .utc()
        .toISOString(),
      type: (localChanges.type ?? activeCalculationParameters.type) as ApiDatabaseCommonCorrelationJobType,
      measurementParameterIds: (localChanges.selectedParameters ?? activeCalculationParameters.selectedParameters)
        .map((techName) => measurementParameters?.externalDtos?.find((p) => p.technicalName === techName)?.id)
        .filter(Boolean) as number[],
      measurementDeviceIds: selectedDeviceIds,
      significanceThreshold:
        (localChanges.type ?? activeCalculationParameters.type) === 0
          ? 0
          : Number(localChanges.threshold ?? activeCalculationParameters.threshold),
    };

    try {
      await submitSelectionForCorrelation(payload).unwrap();

      dispatch(
        setSnackbar({
          open: true,
          type: 'success',
          textContent: t('snackbar.correlation.submit.success'),
        })
      );

      setModalCalculationName('');
      dispatch(resetAll());
    } catch (error: any) {
      console.error('Error submitting correlation job:', error);

      const requestID = error?.data?.traceId || undefined;

      dispatch(
        setSnackbar({
          open: true,
          type: 'error',
          textContent: t('snackbar.correlation.submit.error'),
          copyToClipboard: true,
          requestID,
        })
      );
    } finally {
      dispatch(resetAll());
      handleOpen(false);
    }
  };

  useEffect(() => {
    const prevTsoIds = new Set(prevActiveTsosRef.current.map((tso) => tso.id));
    const newTsos = activeTsos.filter((tso) => !prevTsoIds.has(tso.id));

    const fetchDevicesForTso = async (tsoId: string) => {
      try {
        const result = await triggerGetDeviceHierarchy(tsoId, true);
        const rawTree = result.data?.externalDtos;
        if (!rawTree) return;

        const parsedTree = parseSubstationDevices(rawTree);
        if (!parsedTree) return;

        setTsoDeviceOptions((prev) => ({ ...prev, [tsoId]: parsedTree }));
      } catch (error) {
        console.error('Error on "trigger device hierarchy": ', error);
      }
    };

    const loadMissingTsoDevices = async () => {
      for (const tso of newTsos) {
        if (!tso || tsoDeviceOptions[tso.id]) continue;
        await fetchDevicesForTso(tso.id);
      }
    };

    loadMissingTsoDevices();

    prevActiveTsosRef.current = activeTsos;
  }, [activeTsos, activeTsoName]);

  useEffect(() => {
    setParameterOptions(parameters);
  }, [parameters]);

  useEffect(() => {
    if (!showSubmitModal) {
      setCalculationNameError(null);
      return;
    }

    if (!modalCalculationName) {
      setCalculationNameError(null);
      return;
    }

    const validationResult = validateName(modalCalculationName);
    setCalculationNameError(validationResult);
  }, [modalCalculationName, showSubmitModal]);

  useEffect(() => {
    pendingInitTsosRef.current = new Set((activeTsos ?? []).map((tso) => tso.id.toString()));
    devicesLoaded.current = false;
  }, [activeTsos]);

  useEffect(() => {
    setThresholdValidation(
      activeCalculationParameters.parameterType !== 'multiple' || isThresholdInRange(activeCalculationParameters.threshold)
    );
  }, [activeCalculationParameters.parameterType, activeCalculationParameters.threshold, selectedCalculation]);

  return (
    <StyledWrapper>
      <StyledSectionWrapper>
        <StyledMeasurementDeviceTitle>{t('correlation.startNewCalculation')}</StyledMeasurementDeviceTitle>
        <ChipSelectorTsoPq
          customWidth="30.5rem"
          label={t('correlation.tso')}
          // @ts-ignore
          chips={tsos?.externalDtos ?? defaultTsos}
          error={triedSubmitting && mandatoryFieldsStatus.tso === undefined}
          supportingText={
            triedSubmitting && mandatoryFieldsStatus.tso === undefined ? t('correlation.tso.required') : ''
          }
          activeChips={activeTsos}
          handleChange={handleActiveTsosChange}
          mandatory
        />
        <StyledLineWrapper>
          <DatepickerTsoPq
            label={t('correlation.start')}
            type="datetimepicker"
            width="15rem"
            enableFutureDates={false}
            error={triedSubmitting && mandatoryFieldsStatus.start === undefined}
            supportingText={
              triedSubmitting && mandatoryFieldsStatus.start === undefined ? t('correlation.start.required') : ''
            }
            date={activeCalculationParameters.start}
            handleChange={handleStartChange}
            mandatory
          />
          <DatepickerTsoPq
            label={t('correlation.end')}
            supportingText={
              triedSubmitting && mandatoryFieldsStatus.end === undefined ? t('correlation.end.required') : ''
            }
            type="datetimepicker"
            width="15rem"
            enableFutureDates={false}
            error={triedSubmitting && mandatoryFieldsStatus.end === undefined}
            handleChange={handleEndChange}
            date={activeCalculationParameters.end}
            minDateTime={activeCalculationParameters.start ? dayjs(activeCalculationParameters.start) : undefined}
            mandatory
          />
        </StyledLineWrapper>
        <SegmentedButtonTsoPq
          customWidth="31.5rem"
          centerAlignment
          options={parameterTypeOptions}
          sizeVariant="m"
          handleChange={handleParameterTypeChange}
          defaultValue={activeCalculationParameters.parameterType ?? ''}
        />

        <TreeAutocompleteTsoPq
          customwidth="31.5rem"
          multiple={activeCalculationParameters.parameterType === 'multiple'}
          options={parameterOptions ?? []}
          label={t('measurementVisualization.parameters.measurmentParameters')}
          placeholder={t('general.error.valueSelect')}
          handleChange={handleParametersChange}
          mandatory
          defaultValue={activeCalculationParameters.selectedParameters}
          error={triedSubmitting && !mandatoryFieldsStatus.selectedParameters}
          supportingText={
            triedSubmitting && !mandatoryFieldsStatus.selectedParameters
              ? t('correlation.measurementParameter.required')
              : ''
          }
        />
        {activeCalculationParameters.parameterType === 'multiple' && (
          <TextFieldTsoPq
            label={t('correlation.significanceThreshold')}
            placeholder={t('correlation.enterValue')}
            customwidth="31.5rem"
            mandatory
            error={!thresholdValidation || (triedSubmitting && !mandatoryFieldsStatus.threshold)}
            value={activeCalculationParameters.threshold ?? ''}
            handleChange={(e) => handleThresholdChange(e)}
            supportingText={
              !thresholdValidation || (triedSubmitting && !mandatoryFieldsStatus.threshold)
                ? t('correlation.significanceThreshold.required')
                : ''
            }
            whiteBackground
          />
        )}
      </StyledSectionWrapper>
      {activeTsos?.map((tso) => (
        <StyledSectionWrapper key={tso.id} selectDevice>
          <StyledMeasurementDeviceTitle>{tso.name}</StyledMeasurementDeviceTitle>
          <TreeAutocompleteTsoPq
            customwidth="31.5rem"
            options={tsoDeviceOptions[tso.id] ?? []}
            label={t('measurementVisualization.parameters.measurementDevice')}
            placeholder={t('general.error.valueSelect')}
            handleChange={(values, _checked, isUserChange) => handleDevicesChange(tso.id, values, isUserChange)}
            defaultValue={activeCalculationParameters.deviceTechnicalNames?.[tso.id] ?? []}
            lowestLevel={3}
            multiple
            mandatory
            error={triedSubmitting && Object.values(mandatoryFieldsStatus.selectedDevices).some((device) => !device)}
            supportingText={
              triedSubmitting && Object.values(mandatoryFieldsStatus.selectedDevices).some((device) => !device)
                ? `${t('measurementVisualization.parameters.measurementDevice')} ${t('general.error.required')}`
                : ''
            }
          />
        </StyledSectionWrapper>
      ))}

      <ModalDialogTsoPq
        withExtraField
        title={t('correlation.submitCalculation')}
        modalState={showSubmitModal}
        customMaxWidth="43.1875rem"
        icon
        buttons
        onClose={handleResetInModal}
        actions={
          <>
            <ButtonTsoPq
              buttonType="outlinedPrimary"
              onClick={handleResetInModal}
              textContent={t('correlation.cancel')}
            />
            <ButtonTsoPq
              buttonType="filledPrimary"
              onClick={() => handleCalculationSubmit()}
              textContent={t('correlation.submitCalculation')}
              disabled={!!calculationNameError || !modalCalculationName?.trim()}
            />
          </>
        }
      >
        <TypographyTsoPq typographyType="bodyTextMD">{t('correlation.dialogContent')}</TypographyTsoPq>
        <TextFieldTsoPq
          label={t('correlation.dialogTextfieldTitle')}
          placeholder={t('correlation.dialogTextfieldPlaceholder')}
          customwidth="41rem"
          mandatory
          value={modalCalculationName ?? ''}
          handleChange={(e) => setModalCalculationName(e)}
          supportingText={calculationNameError || t('correlation.dialogTextfieldHint')}
          whiteBackground
          error={!!calculationNameError}
          characterLimit={250}
        />
      </ModalDialogTsoPq>

      <StyledButtonWrapper>
        <ButtonTsoPq
          onClick={handleReset}
          sizeVariant="m"
          buttonType="outlinedPrimary"
          textContent={t('measurementVisualization.form.discard')}
        />
        <PermissionGate roles={userOrSupportRestriction}>
          <ButtonTsoPq
            onClick={handleSubmit}
            sizeVariant="m"
            buttonType="filledPrimary"
            textContent={t('correlation.submitCalculation')}
            disabled={!!selectedCalculation}
          />
        </PermissionGate>
      </StyledButtonWrapper>
    </StyledWrapper>
  );
};

export default CorelationParameters;
